'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';

interface TrackingFormProps {
  onTrack: (trackingNumber: string) => void;
}

export default function TrackingForm({ onTrack }: TrackingFormProps) {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [error, setError] = useState('');

  const validateTrackingNumber = (value: string): boolean => {
    // Format: TRK followed by 9-12 digits
    const regex = /^TRK\d{9,12}$/;
    return regex.test(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    if (!validateTrackingNumber(trackingNumber.toUpperCase())) {
      setError('Invalid tracking number format. Use format: TRK123456789');
      return;
    }

    analytics.trackShipmentSearch(trackingNumber.toUpperCase());
    analytics.trackConversion('shipment_tracked');
    onTrack(trackingNumber.toUpperCase());
  };

  return (
    <div className="bg-card rounded-xl p-8 shadow-card border border-border">
      <h2 className="text-2xl font-heading font-semibold text-foreground mb-6">Track Your Shipment</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="tracking" className="block text-sm font-medium text-foreground mb-2">
            Tracking Number
          </label>
          <div className="relative">
            <Icon 
              name="MagnifyingGlassIcon" 
              size={20} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              id="tracking"
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Enter tracking number (e.g., TRK123456789)"
              className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            />
          </div>
          {error && (
            <p className="mt-2 text-sm text-error flex items-center gap-2">
              <Icon name="ExclamationCircleIcon" size={16} />
              {error}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center gap-2"
        >
          <span>Track Shipment</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>
      </form>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Demo Tracking Numbers:</strong> TRK123456789, TRK987654321, TRK555666777
        </p>
      </div>
    </div>
  );
}